/* eslint-disable max-len */
import React, { FC, useEffect, useRef, useState } from 'react'
import { dateManipulateDay, diffDays, format, nowDate, secToHourMinute } from '../../../utils/date'
import Holidays from 'date-holidays'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Tips from '../common/Tips'
import { PackageBlock } from '../../../states/types/task'

interface Props {
  packageBlock: PackageBlock
  displayDateList: string[]
  updateCallback: (blockType: string, id: number, startDate: string, endDate: string, callback: VoidFunction) => void
  openModalCallback: (blockType: string, id: number) => void
  displayType: string
  displayBlockWidth: number
  bgColor: string
  editable?: boolean
}

const GanttChartScheduleList: FC<Props> = (props) => {
  const {
    packageBlock,
    displayDateList,
    updateCallback,
    openModalCallback,
    displayType,
    displayBlockWidth,
    bgColor,
    editable,
  } = props

  const holidays = new Holidays()
  holidays.init('JP')

  const clickRef = useRef() as React.MutableRefObject<HTMLDivElement>

  const [isMoveDrag, setIsMoveDrag] = useState<boolean>(false)
  const [dragType, setDragType] = useState<string>("")
  const [baseDate, setBaseDate] = useState<string | null>(null)
  const [baseStartDate, setStartBaseDate] = useState<string | null>(null)
  const [baseEndDate, setEndBaseDate] = useState<string | null>(null)
  const [isChange, setIsChange] = useState<boolean>(false)

  const [data, setData] = useState<PackageBlock>()

  const [tmpStartDate, setTmpStartDate] = useState<string | null>(null)
  const [tmpEndDate, setTmpEndDate] = useState<string | null>(null)

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [isSchedule, setIsSchedule] = useState<boolean>(false)

  const reset = () => {
    setIsMoveDrag(false)
    setDragType("")
    setBaseDate(null)
    setStartBaseDate(null)
    setEndBaseDate(null)
    setIsChange(false)
  }

  const endDrag = () => {
    setIsLoading(true)
    if (!isMoveDrag || !isChange) {
      setIsLoading(false)
      reset()
      return
    }
    setIsMoveDrag(false)
    if (tmpStartDate && tmpEndDate && data) {
      updateCallback(
        packageBlock.block_type,
        packageBlock.id,
        tmpStartDate,
        tmpEndDate,
        () => {
          setIsLoading(false)
          setIsSchedule(true)
          reset()
        },
      )
      return
    }
    setIsLoading(false)
    reset()
  }

  const openConfig = () => {
    if (!isMoveDrag || !isChange) {
      openModalCallback(packageBlock.block_type, packageBlock.id)
    }
    reset()
  }

  const createSchedule = (clickPosition: number) => {
    const count = Math.floor(clickPosition / (displayBlockWidth + 1))
    const targetDate = displayDateList[count]
    setIsMoveDrag(true)
    setTmpStartDate(targetDate)
    setDragType("end")
    if (displayType === "weekly") {
      setTmpEndDate(dateManipulateDay(targetDate, 6, "YYYY-MM-DD"))
    } else {
      setTmpEndDate(targetDate)
    }
  }

  const startDrag = (clickPosition: number, type: string) => {
    const count = Math.floor(clickPosition / (displayBlockWidth + 1))
    const targetDate = displayDateList[count]
    setIsMoveDrag(true)
    if (type === "start") {
      setDragType("start")
    } else if (type === "end") {
      setDragType("end")
    } else {
      setDragType("move")
      setBaseDate(targetDate)
      setStartBaseDate(tmpStartDate)
      setEndBaseDate(tmpEndDate)
    }
  }

  const moveOrDragSchedule = (clickPosition: number) => {
    const count = Math.floor(clickPosition / (displayBlockWidth + 1))
    const targetDate = displayDateList[count]
    setIsChange(true)
    if (dragType === "start") {
      setTmpStartDate(targetDate)
    } else if (dragType === "end") {
      if (displayType === "weekly") {
        setTmpEndDate(dateManipulateDay(targetDate, 6, "YYYY-MM-DD"))
      } else {
        setTmpEndDate(targetDate)
      }
    } else {
      const diffCount = diffDays(baseDate || targetDate, targetDate)
      setTmpStartDate(dateManipulateDay(baseStartDate, diffCount, "YYYY-MM-DD"))
      setTmpEndDate(dateManipulateDay(baseEndDate, diffCount, "YYYY-MM-DD"))
    }
  }

  const buildScheduleBlock = () => {
    let startPosition = 0
    let blockWidth = 0
    if (displayType === "daily") {
      if (
        (Number(format(displayDateList[0], "YYYYMMDD")) > Number(format(tmpEndDate, "YYYYMMDD")))
        || (Number(format(displayDateList[displayDateList.length - 1], "YYYYMMDD")) < Number(format(tmpStartDate, "YYYYMMDD")))
      ) {
        return (
          <>
            <div
              style={{
                position: "absolute",
                left: "10px",
                top: "50%",
                color: "#999999",
                fontSize: "10px",
                pointerEvents: "none",
                transform: "translateY(-50%)",
                whiteSpace: "nowrap",
              }}
            >
              {`表示期間外に予定が設定されています。予定:${format(tmpStartDate, "YYYY年M月D日")} ~ ${format(tmpEndDate, "YYYY年M月D日")}`}
            </div>
          </>
        )
      }
      const start = Number(format(displayDateList[0], "YYYYMMDD")) > Number(format(tmpStartDate, "YYYYMMDD")) ? displayDateList[0] : tmpStartDate
      const end = Number(format(displayDateList[displayDateList.length - 1], "YYYYMMDD")) < Number(format(tmpEndDate, "YYYYMMDD")) ? displayDateList[displayDateList.length - 1] : tmpEndDate
      startPosition = diffDays(displayDateList[0], start || nowDate()) * (displayBlockWidth + 1)
      blockWidth = (diffDays(start || nowDate(), end || nowDate()) + 1) * (displayBlockWidth + 1)
    }
    if (displayType === "weekly") {
      if (
        (Number(format(displayDateList[0], "YYYYMMDD")) > Number(format(tmpEndDate, "YYYYMMDD")))
        || (Number(dateManipulateDay(displayDateList[displayDateList.length - 1], 6, "YYYYMMDD")) < Number(format(tmpStartDate, "YYYYMMDD")))
      ) {
        return (
          <>
            <div
              style={{
                position: "absolute",
                left: "10px",
                top: "50%",
                color: "#999999",
                fontSize: "10px",
                pointerEvents: "none",
                transform: "translateY(-50%)",
                whiteSpace: "nowrap",
              }}
            >
              {`表示期間外に予定が設定されています。予定:${format(tmpStartDate, "YYYY年M月D日")} ~ ${format(tmpEndDate, "YYYY年M月D日")}`}
            </div>
          </>
        )
      }
      const start = Number(format(displayDateList[0], "YYYYMMDD")) > Number(format(tmpStartDate, "YYYYMMDD")) ? displayDateList[0] : tmpStartDate
      const end = Number(dateManipulateDay(displayDateList[displayDateList.length - 1], 6, "YYYYMMDD")) < Number(format(tmpEndDate, "YYYYMMDD")) ? dateManipulateDay(displayDateList[displayDateList.length - 1], 6) : tmpEndDate
      startPosition = (diffDays(displayDateList[0], start || nowDate()) / 7) * (displayBlockWidth + 1)
      blockWidth = ((diffDays(start || nowDate(), end || nowDate()) + 1) / 7) * (displayBlockWidth + 1)
    }
    const backgroundColor = isMoveDrag ? "#96C0D8" : "#9CD8D8"
    return (
      <>
        <div
          style={{
            position: "absolute",
            left: `${startPosition + 10}px`,
            top: "50%",
            transform: "translateY(-50%)",
            color: "#666666",
            fontSize: "10px",
            zIndex: 2,
            pointerEvents: "none",
            width: `${blockWidth - 10}px`,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            transitionDuration: "100ms",
          }}
        >
          {packageBlock.name}
        </div>
        <div
          style={{
            position: "absolute",
            left: `${startPosition}px`,
            top: "50%",
            width: "10px",
            height: "36px",
            padding: 0,
            backgroundColor: backgroundColor,
            borderRadius: "4px 0 0 4px",
            transform: "translateY(-50%)",
            zIndex: 1,
            // eslint-disable-next-line no-nested-ternary
            cursor: editable ? isMoveDrag ? "grabbing" : "col-resize" : "not-allowed",
            opacity: "0.7",
            transitionDuration: "100ms",
          }}
          onMouseDown={(e) => editable && startDrag(e.clientX - clickRef.current.getBoundingClientRect().left, "start")}
          role="none"
        />
        <div
          style={{
            position: "absolute",
            left: `${startPosition + 10}px`,
            top: "50%",
            width: `${blockWidth - 20}px`,
            height: "36px",
            padding: 0,
            backgroundColor: backgroundColor,
            transform: "translateY(-50%)",
            zIndex: 1,
            // eslint-disable-next-line no-nested-ternary
            cursor: editable ? isMoveDrag ? "grabbing" : "move" : "not-allowed",
            opacity: "0.7",
            transitionDuration: "100ms",
          }}
          onMouseDown={(e) => editable && startDrag(e.clientX - clickRef.current.getBoundingClientRect().left, "move")}
          onMouseUp={() => editable && openConfig()}
          role="none"
        />
        <div
          style={{
            position: "absolute",
            left: `${startPosition + blockWidth - 10}px`,
            top: "50%",
            width: "10px",
            height: "36px",
            padding: 0,
            backgroundColor: backgroundColor,
            borderRadius: "0 4px 4px 0",
            transform: "translateY(-50%)",
            zIndex: 1,
            // eslint-disable-next-line no-nested-ternary
            cursor: editable ? isMoveDrag ? "grabbing" : "col-resize" : "not-allowed",
            opacity: "0.7",
            transitionDuration: "100ms",
          }}
          onMouseDown={(e) => editable && startDrag(e.clientX - clickRef.current.getBoundingClientRect().left, "end")}
          role="none"
        />
      </>
    )
  }

  const buildActualBlock = (actual: {date: string, time: number}[]) => {
    if (!actual.length) return (<></>)
    return (
      <>
        {
          actual.map((v) => {
            let startPosition = 0
            let blockWidth = 0
            if (displayType === "daily") {
              if (
                (Number(format(displayDateList[0], "YYYYMMDD")) > Number(format(v.date, "YYYYMMDD")))
                || (Number(format(displayDateList[displayDateList.length - 1], "YYYYMMDD")) < Number(format(v.date, "YYYYMMDD")))
              ) return (<React.Fragment key={`${data?.name}_${v.date}_${v.time}`} />)
              startPosition = diffDays(displayDateList[0], v.date) * (displayBlockWidth + 1)
              blockWidth = displayBlockWidth + 1
            }
            if (displayType === "weekly") {
              if (
                (Number(format(displayDateList[0], "YYYYMMDD")) > Number(format(v.date, "YYYYMMDD")))
                || (Number(dateManipulateDay(displayDateList[displayDateList.length - 1], 6, "YYYYMMDD")) < Number(format(v.date, "YYYYMMDD")))
              ) return (<React.Fragment key={`${data?.name}_${v.date}_${v.time}`} />)
              startPosition = (diffDays(displayDateList[0], v.date) / 7) * (displayBlockWidth + 1)
              blockWidth = (displayBlockWidth + 1) / 7
            }
            return (
              <Tips
                key={`${data?.name}_${v.date}_${v.time}`}
                description={`${format(v.date, "M月D日")} ${data?.name}%br%実績時間 ${secToHourMinute(v.time)}`}
                className=""
                icon={false}
                style={{
                  position: "absolute",
                  left: `${startPosition}px`,
                  top: "75%",
                  width: `${blockWidth}px`,
                  height: "10px",
                  padding: 0,
                  backgroundColor: "rgba(178, 28, 28, 0.5)",
                  borderRadius: 0,
                  transform: "translateY(-50%)",
                  zIndex: 2,
                }}
                innerStyle={{
                  top: "-5px",
                }}
              >
                <>
                  <div
                    style={{
                      width: `${blockWidth}px`,
                    }}
                  />
                </>
              </Tips>
            )
          })
        }
      </>
    )
  }

  const saveLoading = () => {
    let startPosition = diffDays(displayDateList[0], tmpStartDate || nowDate()) * (displayBlockWidth + 1)
    let blockWidth = (diffDays(tmpStartDate || nowDate(), tmpEndDate || nowDate()) + 1) * (displayBlockWidth + 1)
    if (displayType === "weekly") {
      startPosition = (diffDays(displayDateList[0], tmpStartDate || nowDate()) / 7) * (displayBlockWidth + 1)
      blockWidth = ((diffDays(tmpStartDate || nowDate(), tmpEndDate || nowDate()) + 1) + 7) * (displayBlockWidth + 1)
    }
    return (
      <>
        <div
          style={{
            position: "absolute",
            width: "100%",
            minHeight: "40px",
            backgroundColor: "rgba(255, 255, 255, 0.5)",
            zIndex: 2,
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: `${startPosition + blockWidth}px`,
            transform: "translateY(-50%)",
            display: "flex",
            gap: "4px",
            padding: "0px 8px",
            zIndex: 1,
            width: "max-content",
            fontSize: "10px",
          }}
        >
          <FontAwesomeIcon icon={['fas', 'spinner']} spin />
          保存しています…
        </div>
      </>
    )
  }

  useEffect(() => {
    setData(packageBlock)
  }, [packageBlock])

  useEffect(() => {
    if (!data) return
    setTmpStartDate(data.schedule.start_date || null)
    setTmpEndDate(data.schedule.end_date || null)
    setIsSchedule(!!data.schedule.start_date && !!data.schedule.end_date)
  }, [data])

  useEffect(() => {
    if (tmpStartDate && tmpEndDate && data && Number(format(tmpStartDate, "YYYYMMDD")) > Number(format(tmpEndDate, "YYYYMMDD"))) {
      if (dragType === "end") {
        setTmpEndDate(tmpStartDate)
        setDragType("start")
      } else if (dragType === "start") {
        setTmpStartDate(tmpEndDate)
        setDragType("end")
      }
    }
  }, [tmpStartDate, tmpEndDate])

  return (
    <>
      <div
        ref={clickRef}
        style={{
          position: "relative",
          marginTop: "-1px",
          width: `${(displayDateList.length * (displayBlockWidth + 1) + 1)}px`,
          height: "40px",
          borderBottom: "solid 1px #DDDDDD",
          userSelect: "none",
          // eslint-disable-next-line no-nested-ternary
          background: editable ? isMoveDrag ? "rgba(156, 216, 216, 0.2)" : "none" : `linear-gradient(to top, #EEEEEE 38px, #DDDDDD 1px)`,
          cursor: editable ? "default" : "not-allowed",
          zIndex: 2,
        }}
        role="none"
        onMouseDown={(e) => editable && !isSchedule && createSchedule(e.clientX - clickRef.current.getBoundingClientRect().left)}
        onMouseMove={(e) => editable && isMoveDrag && moveOrDragSchedule(e.clientX - clickRef.current.getBoundingClientRect().left)}
        onMouseUp={() => editable && endDrag()}
        onMouseLeave={() => editable && endDrag()}
      >
        {isLoading && saveLoading()}
        {tmpStartDate && tmpEndDate && buildScheduleBlock()}
        {buildActualBlock(data?.actual || [])}
      </div>
    </>
  )
}

GanttChartScheduleList.defaultProps = {
  editable: true,
}

export default GanttChartScheduleList
